import React, { useCallback } from 'react';

import { Collapse, List, ListItem } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';

import ProtectedData, { UNKNOWN } from 'pages/item/v2/components/ProtectedData';
import isEmpty from 'utils/is-empty';

interface Bug {
    id: string;
    name: string;
    description: string;
    severity: string;
}

interface Props {
    data: {
        hackapp?: {
            name: string;
            vendor: string;
            link: string;
            apk: string;
            store: string;
            version: number;
            bugs: Bug[];
        };
    };
}

const HackApp: React.FC<Props> = ({ data: { hackapp } }) => {
    const renderBugItem = useCallback((key: string, data: Bug[]) => {
        if (isEmpty(data)) {
            return null;
        }

        const mappings = {
            critical: { className: 'vulners-background-red', name: 'CRITICAL' },
            medium: { className: 'vulners-background-yellow', name: 'MEDIUM' },
            notice: { className: 'vulners-background-grey', name: 'NOTICE' }
        };

        return (
            <React.Fragment key={key}>
                <ListItem className={mappings[key].className}>
                    <ListItemText primary={<ProtectedData data={mappings[key]?.name ?? UNKNOWN} />} />
                </ListItem>
                <Collapse in timeout="auto" unmountOnExit>
                    {data.map((bug, index) => (
                        <ListItem key={index}>
                            <ListItemText
                                primary={<ProtectedData data={bug.name} />}
                                secondary={<ProtectedData data={bug.description} />}
                            />
                        </ListItem>
                    ))}
                </Collapse>
            </React.Fragment>
        );
    }, []);

    const renderBugs = useCallback(
        (bugs: Bug[]) => {
            const vector = bugs.reduce((output, bug) => {
                output[bug.severity] ||= [];
                output[bug.severity].push(bug);
                return output;
            }, {} as Record<string, Bug[]>);

            return <List>{Object.entries(vector).map(([key, data]) => renderBugItem(key, data))}</List>;
        },
        [renderBugItem]
    );

    if (!hackapp) {
        return null;
    }

    return (
        <div>
            <h4>{'Name'}</h4>
            <ProtectedData data={hackapp.name} />
            <br />
            <h4>{'Vendor'}</h4>
            <ProtectedData data={hackapp.vendor} />
            <br />
            <h4>{'Link'}</h4>
            <ProtectedData data={hackapp.link}>
                <a href={hackapp.link} rel="nofollow noreferrer" target="_blank">
                    {hackapp.apk}
                </a>
            </ProtectedData>
            <br />
            <h4>{'Store'}</h4>
            <ProtectedData data={hackapp.store} />
            <br />
            <h4>{'Version'}</h4>
            <ProtectedData data={hackapp.version} />
            <br />
            {renderBugs(hackapp.bugs)}
        </div>
    );
};

export default HackApp;
